/**
 * StepStatusPill (BLU-1376, handover 4.2): the state word on the step pane.
 *
 * Word, icon and border style all change with the state, so the pill reads
 * without colour: Up next (brand tint), To do (plain), Done (tick, green tint),
 * Skipped (slash icon, dashed border), Locked (lock), Waiting for your 3PL
 * (clock, amber).
 */
import { useTranslation } from 'react-i18next';
import { Ban, Check, Clock, Lock, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { StepVisualState } from '../../lib/step-state';

const PILL_CLASS: Record<StepVisualState, string> = {
  current: 'border-threadheads-purple/40 bg-accent text-primary-strong',
  open: 'border-line-strong bg-background text-muted-foreground',
  done: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  skipped: 'border-dashed border-line-strong bg-background text-muted-foreground',
  locked: 'border-border bg-muted text-muted-foreground',
  waiting: 'border-amber-300 bg-amber-50 text-amber-800',
};

const PILL_ICON: Partial<Record<StepVisualState, LucideIcon>> = {
  done: Check,
  skipped: Ban,
  locked: Lock,
  waiting: Clock,
};

interface StepStatusPillProps {
  state: StepVisualState;
  className?: string;
}

export function StepStatusPill({ state, className }: StepStatusPillProps) {
  const { t } = useTranslation('onboarding');
  const Icon = PILL_ICON[state];

  return (
    <span
      data-state={state}
      className={cn(
        'inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-2 text-xs font-medium',
        PILL_CLASS[state],
        className,
      )}
    >
      {Icon && <Icon className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />}
      {t(`shell.pill.${state}`)}
    </span>
  );
}
